import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { siteConfig } from "../config/siteConfig";
import LanguageSelector from "./LanguageSelector";
import Logo from "./Logo";
import {
  Menu,
  X,
  PhoneCall,
  ChevronRight,
  MessageCircle,
  MapPin,
  Phone,
} from "lucide-react";

const Navbar = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, lPath, isRTL } = useLanguage();
  const location = useLocation();

  // Primary 6 Desktop Navigation Items
  const navLinks = [
    { path: "/", label: t("nav.home") || "Home" },
    { path: "/about", label: t("nav.about") || "About Us" },
    { path: "/wool-specifications", label: t("nav.ourWool") || "Our Wool" },
    { path: "/for-yarn-manufacturers", label: t("nav.ourSupply") || "Our Supply" },
    { path: "/wool-supplier-bikaner", label: t("nav.infrastructure") || "Infrastructure" },
    { path: "/contact", label: t("nav.contact") || "Contact" },
  ];

  // Secondary Sourcing Hub Links for Sidebar Drawer
  const secondaryLinks = [
    { path: "/carpet-wool-supplier-india", label: "Carpet Wool (30+ & 32-34+ Micron)" },
    { path: "/raw-wool-supplier-india", label: "Raw Wool Supplier India" },
    { path: "/wool-agent-bikaner", label: "Wool Agent in Bikaner Mandi" },
    { path: "/bulk-wool-supply", label: "Bulk Wool Supply" },
    { path: "/wool-supply-rajasthan", label: "Wool Supply in Rajasthan" },
    { path: "/international-sourcing", label: "International Sourcing" },
    { path: "/partners", label: "Leadership & Partners" },
  ];

  // Active link detection
  const isActive = (path) => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const supportedLangs = ["en", "en-au", "en-nz", "ar", "zh-cn"];
    if (pathParts.length > 0 && supportedLangs.includes(pathParts[0].toLowerCase())) {
      pathParts.shift();
    }
    const currentSubpath = "/" + pathParts.join("/");

    if (path === "/" && currentSubpath === "/") return true;
    if (path !== "/" && currentSubpath.startsWith(path)) return true;
    return false;
  };

  // Scroll listener for sticky glass navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll and handle Escape key when drawer is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDrawerOpen]);

  // Close drawer on route change
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
          isScrolled ? "glass-navbar-scrolled shadow-md" : "glass-navbar"
        }`}
      >
        {/* Top Trust / Announcement Bar */}
        <div
          className={`bg-wool-charcoal text-wool-sand text-[11px] font-medium px-4 border-b border-wool-charcoal/80 transition-all duration-300 ${
            isScrolled ? "py-0.5 opacity-95" : "py-1"
          }`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
              <span className="font-semibold text-white">Established 1965</span>
              <span className="hidden sm:inline text-wool-sand/60">·</span>
              <span className="hidden sm:inline text-wool-sand/80">Care of Bulakichand Kesharichand</span>
              <span className="hidden md:inline text-wool-sand/60">·</span>
              <span className="hidden md:inline text-wool-sand/80">Bikaner, Rajasthan</span>
            </div>
            <div className="flex items-center gap-4 text-[11px]">
              <a
                href={`tel:${siteConfig.contacts.primaryPhone}`}
                className="hidden sm:flex items-center gap-1 hover:text-white transition"
              >
                <PhoneCall className="w-3 h-3 text-emerald-400" />
                <span>+91 {siteConfig.contacts.primaryPhone}</span>
              </a>
              <span className="text-wool-sand/90 font-medium">B2B Raw Wool Supply</span>
            </div>
          </div>
        </div>

        {/* Main Navbar */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div
            className={`flex items-center justify-between transition-all duration-300 ${
              isScrolled ? "h-16 sm:h-18" : "h-18 sm:h-20"
            }`}
          >
            {/* Logo / Brand Name on the Left */}
            <Link
              to={lPath("/")}
              className="flex items-center py-1 group focus:outline-none focus-visible:ring-2 focus-visible:ring-wool-bronze rounded-lg min-w-0"
              onClick={() => setIsDrawerOpen(false)}
            >
              <Logo variant="full" className="h-8 sm:h-9 md:h-10" />
            </Link>

            {/* Desktop Horizontal Navigation (Screens >= 1280px / xl) */}
            <nav
              aria-label="Main Navigation"
              className="hidden xl:flex items-center gap-1.5 text-[13px] font-medium text-wool-slate"
            >
              {navLinks.map((link) => {
                const active = isActive(link.path);
                return (
                  <Link
                    key={link.path}
                    to={lPath(link.path)}
                    className={`px-3.5 py-2 rounded-xl transition-all duration-200 whitespace-nowrap ${
                      active
                        ? "text-wool-bronze-dark font-semibold bg-wool-sand/70 shadow-sm"
                        : "hover:text-wool-bronze hover:bg-wool-sand/35"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop Right Actions (Language Selector & WhatsApp) */}
            <div className="hidden xl:flex items-center gap-3">
              <LanguageSelector />
              <a
                href={siteConfig.contacts.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 shadow-sm transition-transform hover:scale-102"
              >
                <MessageCircle className="w-3.5 h-3.5 text-emerald-200" />
                <span>WhatsApp</span>
              </a>
            </div>

            {/* Mobile & Tablet Right Controls (Screens < 1280px) */}
            <div className="flex xl:hidden items-center gap-1.5 sm:gap-2 shrink-0">
              <LanguageSelector />
              <button
                type="button"
                onClick={() => setIsDrawerOpen(true)}
                className="p-2 sm:p-2.5 text-wool-charcoal bg-white/70 hover:bg-wool-sand/70 border border-wool-border rounded-xl transition shadow-sm focus:outline-none focus:ring-2 focus:ring-wool-bronze/40 shrink-0"
                aria-label="Open navigation menu"
                aria-expanded={isDrawerOpen}
                aria-controls="mobile-sidebar-drawer"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile & Tablet Sidebar Navigation Drawer */}
      {isDrawerOpen && (
        <div className="xl:hidden fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 bg-wool-charcoal/50 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in"
            onClick={() => setIsDrawerOpen(false)}
            aria-hidden="true"
          />

          {/* Side Drawer Panel */}
          <aside
            id="mobile-sidebar-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation Menu"
            className={`fixed top-0 bottom-0 right-0 w-full sm:w-[380px] max-w-[88vw] h-full sidebar-glass border-s border-wool-border flex flex-col justify-between z-50 shadow-2xl animate-in slide-in-from-right duration-300 ${
              isRTL ? "left-0 right-auto border-e border-s-0 slide-in-from-left" : ""
            }`}
          >
            {/* Drawer Header */}
            <div className="p-4 sm:p-5 border-b border-wool-border/70 flex items-center justify-between bg-white/60">
              <Link
                to={lPath("/")}
                onClick={() => setIsDrawerOpen(false)}
                className="flex items-center min-w-0"
              >
                <Logo variant="full" className="h-8 sm:h-9" />
              </Link>
              <button
                type="button"
                onClick={() => setIsDrawerOpen(false)}
                className="p-2 rounded-xl text-wool-charcoal hover:bg-wool-sand/60 transition focus:outline-none focus:ring-2 focus:ring-wool-bronze/40 shrink-0"
                aria-label="Close navigation menu"
              >
                <X className="w-5 h-5 text-wool-charcoal" />
              </button>
            </div>

            {/* Drawer Navigation Content (Scrollable) */}
            <div className="flex-1 overflow-y-auto px-4 py-5 space-y-6">
              {/* Primary Nav Links */}
              <div>
                <div className="text-[11px] font-bold text-wool-bronze uppercase tracking-wider px-3 mb-2">
                  Navigation
                </div>
                <nav className="flex flex-col space-y-1">
                  {navLinks.map((link) => {
                    const active = isActive(link.path);
                    return (
                      <Link
                        key={link.path}
                        to={lPath(link.path)}
                        onClick={() => setIsDrawerOpen(false)}
                        className={`flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition ${
                          active
                            ? "bg-wool-sand text-wool-bronze-dark border-s-4 border-wool-bronze shadow-sm"
                            : "text-wool-charcoal hover:bg-white/80 hover:text-wool-bronze"
                        }`}
                      >
                        <span>{link.label}</span>
                        <ChevronRight
                          className={`w-4 h-4 text-wool-muted ${isRTL ? "rotate-180" : ""}`}
                        />
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Secondary Sourcing Hubs Section */}
              <div className="pt-2 border-t border-wool-border/60">
                <div className="text-[11px] font-bold text-wool-muted uppercase tracking-wider px-3 mb-2">
                  Sourcing & Supply Hubs
                </div>
                <div className="flex flex-col space-y-1">
                  {secondaryLinks.map((subLink) => {
                    const active = isActive(subLink.path);
                    return (
                      <Link
                        key={subLink.path}
                        to={lPath(subLink.path)}
                        onClick={() => setIsDrawerOpen(false)}
                        className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg text-xs font-medium transition ${
                          active
                            ? "bg-wool-sand/60 text-wool-bronze-dark font-semibold"
                            : "text-wool-slate hover:bg-white/60 hover:text-wool-bronze"
                        }`}
                      >
                        <span>{subLink.label}</span>
                        <ChevronRight
                          className={`w-3.5 h-3.5 text-wool-muted/70 ${isRTL ? "rotate-180" : ""}`}
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Drawer Footer Actions */}
            <div className="p-5 border-t border-wool-border/70 bg-white/70 space-y-3">
              <a
                href={siteConfig.contacts.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full py-3 text-xs inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-600 rounded-xl font-semibold shadow-sm"
                onClick={() => setIsDrawerOpen(false)}
              >
                <MessageCircle className="w-4 h-4 text-emerald-200" />
                <span>Chat on WhatsApp</span>
              </a>

              <a
                href={`tel:${siteConfig.contacts.primaryPhone}`}
                className="btn-secondary w-full py-2.5 text-xs inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-wool-border font-semibold text-wool-charcoal hover:bg-wool-sand/40"
              >
                <Phone className="w-3.5 h-3.5 text-wool-bronze" />
                <span>Call Desk: +91 {siteConfig.contacts.primaryPhone}</span>
              </a>

              <div className="pt-2 flex items-start gap-1.5 text-[11px] text-wool-muted text-center justify-center">
                <MapPin className="w-3 h-3 text-wool-bronze shrink-0 mt-0.5" />
                <span>B-26, Un Mandi, Antyodaya Nagar, Bikaner, Rajasthan</span>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default Navbar;
